"use client";

import React from "react";
import { ShieldCheck, Stethoscope, BookOpen, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const standards = [{
  icon: Stethoscope,
  title: "Veterinary-reviewed health guidance",
  description: "Every symptom matrix, clinical topic, and preventive protocol undergoes rigorous peer review by licensed veterinary professionals before publication."
}, {
  icon: ShieldCheck,
  title: "Unbiased nutritional analysis",
  description: "Our canine and feline diet analyses assess ingredient biology and peer-reviewed clinical research without brand sponsorship bias."
}, {
  icon: BookOpen,
  title: "Documented medical references",
  description: "We cite established veterinary textbooks, published clinical trials, and certified epidemiology data in every health guide."
}];
export default function Story() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const staggerContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  return <section id="our-story" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Top Eyebrow and Headline Area */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={fadeUp} className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted border border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
            <CheckCircle2 className="size-3.5 text-foreground" />
            <span>Editorial Standards &amp; Origin</span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-tight text-foreground mb-6">
            About PetsWhirl
          </h1>

          <p className="font-serif italic text-xl sm:text-2xl text-foreground/90 font-normal leading-relaxed border-l-2 border-border pl-4 sm:pl-6 py-1">
            PetsWhirl helps pet owners make better-informed decisions through clear, useful and trustworthy information about dogs and cats.
          </p>
        </motion.div>

        {/* Two-Column Editorial Story & Visual Imagery */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16 md:mb-20">
          {/* Narrative Column */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUp} className="lg:col-span-7 space-y-6 text-foreground/80 font-sans leading-relaxed text-base sm:text-lg">
            <p>
              PetsWhirl was founded to bridge the gap between complex veterinary science and daily companion animal care. In an internet crowded with anecdotal pet blogs and unverified advice, we built an editorial publication that values clinical precision, transparent sourcing, and calm clarity.
            </p>

            <p>
              We treat pet health with the seriousness of human medicine. Our writers collaborate directly with practicing veterinary surgeons, board-certified animal nutritionists, and certified behavioral consultants to ensure every recommendation is safe, practical, and grounded in current research.
            </p>

            {/* Clinical Governance Callout */}
            <div className="bg-card border-y sm:border sm:rounded-xl border-border p-6 sm:p-7 relative overflow-hidden">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-foreground" />
              <div className="flex items-start gap-4">
                <ShieldCheck className="size-6 text-foreground shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <h2 className="font-sans text-base sm:text-lg font-semibold text-foreground">
                    Our Clinical Governance Guarantee
                  </h2>
                  <p className="text-sm text-muted-foreground leading-normal">
                    We never publish unverified health claims, untested remedies, or commercial endorsements disguised as medical guidance. Every guide includes reviewer credentials and citation timestamps.
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-md">
                <Link to="/PetHealthPage">
                  Explore health guides
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md">
                <Link to="/NutritionPage">
                  Nutrition standards
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Documentary Imagery Column */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUp} className="lg:col-span-5 relative">
            <div className="rounded-xl overflow-hidden border border-border bg-card shadow-sm">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/about_editorial_team_consultation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/about_editorial_team_consultation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/about_editorial_team_consultation.png-wvc-sizes" alt="PetsWhirl veterinary editorial team reviewing canine diagnostic health information" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 object-cover" />
              <div className="p-5 border-t border-border bg-card">
                <p className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Our editorial board works alongside veterinary specialists in clinical consultation rooms to verify health protocols prior to publication.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Pillars / Editorial Framework Grid */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {standards.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.title} data-index={i} variants={fadeUp}>
                <Card className="h-full border border-border bg-card rounded-xl shadow-2xs hover:shadow-sm transition-[box-shadow,border-color] duration-200 hover:border-border/80">
                  <CardContent className="p-6 sm:p-7 flex flex-col h-full">
                    <div className="size-11 rounded-lg bg-muted border border-border flex items-center justify-center text-foreground mb-5 shrink-0">
                      <Icon className="size-5" />
                    </div>
                    <h3 className="font-sans text-base sm:text-lg font-semibold text-foreground mb-2.5">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-auto">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}